import type { CSSProperties } from 'react'
import { Box, Button, Card, Checkbox, Text } from '@grovus/design-system'
import { accountDetail, type AccountId, type ClientAccount } from '../data/account'
import type { Dict } from '../i18n'
import { fmt } from '../lib/format'
import { AccountTicks } from '../components/Ticks'
import type { DisclosurePanels } from '../components/useDisclosurePanel'

export const ACCOUNTS_PANEL = 'accounts'

/** The shell's account selection, handed to the screen so the filter bar can change it. */
export interface AccountToggleProps {
  accounts: ClientAccount[]
  checked: Record<AccountId, boolean>
  onToggle: (id: AccountId) => void
  /** Checks every account of the active client: the server's default scope. */
  onSelectAll: () => void
}

interface Props extends AccountToggleProps {
  t: Dict
  panels: DisclosurePanels
}

/**
 * One toggle per ad account, in the filter bar (GRO-64, review remark 17).
 *
 * The same selection the header's account panel changes, not a second one: the checkboxes
 * write the shell's map, so the header summary, both screens and both counts always agree.
 * Only the toggles are repeated here; connection states and demo removal stay in the
 * header panel, their home since GRO-41.
 *
 * The last checked account cannot be cleared from here. An empty selection replaces the
 * screen with its empty state, filter bar included, and a control that removed itself would
 * leave no way back beside it. The header still allows it.
 */
export function AccountToggles({ t, accounts, checked, onToggle, panels }: Props) {
  const active = accounts.filter((account) => checked[account.id] !== false)
  const summary = accounts.length > 0 && active.length === accounts.length
    ? fmt(t.accMerged, { n: accounts.length })
    : fmt(t.accOf, { n: active.length, t: accounts.length })
  const open = panels.isOpen(ACCOUNTS_PANEL)

  return (
    <div className="page-anchor">
      <Button
        variant="default"
        {...panels.triggerProps(ACCOUNTS_PANEL)}
        leftSection={<AccountTicks count={active.length} total={accounts.length} />}
        data-testid="filter-accounts-toggle"
      >
        {summary}
      </Button>
      {open && (
        <Card
          role="group"
          aria-label={t.adAccounts}
          className="page-panel page-panel-start"
          style={{ '--panel-width': '300px' } as CSSProperties}
          data-testid="filter-accounts"
          {...panels.panelProps(ACCOUNTS_PANEL)}
        >
          {accounts.map((account) => {
            const on = checked[account.id] !== false
            const last = on && active.length === 1
            const detail = accountDetail(account)
            return (
              <Box key={account.id} py={4} title={last ? t.accLastOne : undefined}>
                <Checkbox
                  checked={on}
                  disabled={last}
                  onChange={() => onToggle(account.id)}
                  data-testid={`filter-account-${account.id}`}
                  // The demo marker and the currency and portfolio sit in the label, so
                  // they are part of the checkbox's accessible name, as in the header panel.
                  label={(
                    <Box component="span" display="block">
                      <Text component="span" display="block" fz="sm" fw={600}>{account.isDemo ? t.demoMarkLong : account.name}</Text>
                      {detail && (
                        <Text component="span" display="block" fz="xs" className="grv-muted" data-testid="account-detail">{detail}</Text>
                      )}
                    </Box>
                  )}
                />
              </Box>
            )
          })}
        </Card>
      )}
    </div>
  )
}
